/**
 * Shared interfaces for the Purchase Manager supplier components.
 * These interfaces are used by both the parent PmPriceDiscoveryComponent
 * and all supplier child components (Hyperpure, DMart, Udaan).
 */

export interface SearchItem {
    name: string;
    /** Clean item name sent to search when the sidebar label includes the unit. */
    sheetItemName?: string;
    /** Sheet unit. Sent with the Purchase Manager search and used for the exact match. */
    unit?: string;
    /** Quantity the Hyperpure cart should hold after this search finishes. */
    targetQuantity?: number;
    /** Set once the price-discovery page has applied the Hyperpure quantity. */
    sheetCartHandled?: boolean;
    /** Upload run this row belongs to, so the agent screen only shows that sheet. */
    sheetRun?: number;
    /** Live status while an uploaded sheet is being searched. */
    sheetState?: 'searching' | 'matched' | 'added' | 'already' | 'found' | 'missed';
    /** Hyperpure product chosen for this sheet row. */
    sheetMatchName?: string;
    sheetMatchSku?: string;
    sheetMatchRate?: number;
    priceRange?: string;
    minPrice?: number;
    maxPrice?: number;
    isLoading?: boolean;
    requestId?: string;
    bestMatch?: SupplierProduct | null;
    preferredBestMatch?: SupplierProduct | null; // API's recommended autofill item
}

export interface BulkPrice {
    qty: number;
    price: number;
    type?: string;
    label?: string;
    displayOnly?: boolean;
}

export interface AssureProductBadge {
    type: string;
    text: string;
    backgroundColor: string;
    textColor: string;
    iconText: string;
}

export function assureVariantUnit(item: {
    skuQuantityWithCombo?: string;
    unitOfMeasure?: string;
    variantQuantity?: string | number;
    unit?: string;
}): string {
    const combo = String(item?.skuQuantityWithCombo || '').trim();
    if (combo) {
        return combo;
    }
    const measure = String(item?.unitOfMeasure || '').trim();
    const quantity = item?.variantQuantity === undefined || item?.variantQuantity === null
        ? ''
        : String(item.variantQuantity).trim();
    if (measure) {
        return `${quantity} ${measure}`.trim();
    }
    return quantity || String(item?.unit || '');
}

export function assureCartTotal(items: Array<{
    itemOfferPriceTotal?: number | null;
    rate?: number;
    selectedQuantity?: number;
    quantity?: number;
}>): number {
    const sum = (items || []).reduce((total, item) => {
        const raw = item?.itemOfferPriceTotal;
        if (raw !== null && raw !== undefined) {
            const line = Number(raw);
            if (Number.isFinite(line)) {
                return total + line;
            }
        }
        const qty = Number(item?.selectedQuantity ?? item?.quantity ?? 0);
        return total + (Number(item?.rate) || 0) * (Number.isFinite(qty) ? qty : 0);
    }, 0);
    return Math.round(sum);
}

export interface SupplierProduct {
    item_name: string;
    sku_id: string;
    rate: number;
    baseRate?: number; // Original non-bulk rate
    basePrice?: number; // Assure search display price, shown struck through beside rate
    mrp?: number;
    price?: number;
    unit: string;
    quantity: number;
    image_url?: string;
    product_images?: string; // Raw field from API (Udaan, DMart)
    platform: string;
    isBestMatch: boolean;
    is_in_stock?: boolean;
    bulkPrices?: BulkPrice[];
    assureBadge?: AssureProductBadge | null;
    supplierName?: string;
    searchItemName?: string;
    selectedQuantity?: number;
    category_id?: string;
    vertical?: string;
    isSelected?: boolean; // For click selection state
    supplierId?: string;
    cartError?: string; // Error message when adding to cart fails
    isAddingToCart?: boolean; // Loading state for API call
    minimumOrderQty?: number; // Minimum quantity allowed for ordering
    maximumOrderQty?: number; // Maximum quantity allowed for ordering
    avlOrderQty?: number; // Available order quantity (Udaan)
    sku?: string; // DeliverIt catalog SKU
    product_number?: string; // Product number from Hyperpure search response / DeliverIt catalog SKU
    article_number?: string; // Article number from D-mart (for favorites)
    dmartArticleNumber?: string; // D-mart article number (alias)
    cart_price_per_unit?: string; // e.g. "₹210.60 (incl. 5.3% tax)" from Hyperpure cart API
    itemOfferPriceTotal?: number;
    skuQuantityWithCombo?: string;
    unitOfMeasure?: string;
    variantQuantity?: string | number;
    swiggyItemId?: string;
    swiggyProductId?: string;
    swiggySpin?: string;
    purchaseStoreId?: string;
}

export interface SupplierSection {
    id: string;
    name: string;
    logo?: string;
    color: string;
    items: SupplierProduct[];
    totalItems: number;
    isVisible: boolean;
    isLocalSupplier?: boolean;
}

export interface MarketBestPrice {
    item_name: string;
    supplier: string;
    supplierId?: string;
    rate: number;
    mrp?: number;
    unit: string;
    image_url?: string;
    product_images?: string;
    savings?: string;
    savingsPercent?: number;
    originalItem?: SupplierProduct;
    originalSection?: SupplierSection;
}

/**
 * Event emitted when a supplier child updates its cart state.
 */
export interface CartUpdateEvent {
    platform: string;
    cartCount: number;
    cartItems: SupplierProduct[];
}

/**
 * Event emitted when a supplier child requests checkout.
 */
export interface CheckoutEvent {
    platform: string;
    items: SupplierProduct[];
}
